<template>
  <div class="metro-security-timeline-container">
    <div 
      ref="timelineRef"
      class="security-timeline"
      @mouseenter="pauseOnHover"
      @mouseleave="resumeOnLeave"
    >
      <div class="timeline-container">
        <div 
          v-for="(event, index) in timelineData" 
          :key="index"
          class="timeline-item"
          :class="{ 'highlight': event.highlight }"
        >
          <div class="timeline-content">
            <div class="timeline-year">{{ event.year }}</div>
            <div class="timeline-title">{{ event.title }}</div>
            <div class="timeline-desc">{{ event.description }}</div>
            <div v-if="event.detail" class="timeline-detail">{{ event.detail }}</div>
            <div class="card-data-source">{{ event.source }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'

interface TimelineEvent {
  year: string
  title: string
  description: string
  detail?: string
  highlight: boolean
  source: string
}

const timelineRef = ref<HTMLElement | null>(null)
const isAutoScrolling = ref(true) // 默认开启自动滚动
const scrollSpeed = ref(1) // 将速度减慢一倍，从2改为1
const animationId = ref<number | null>(null)
const isPausedByHover = ref(false)
const isWaitingForRestart = ref(false) // 新增：标记是否在等待重启

const timelineData: TimelineEvent[] = [
  {
    year: '2002年',
    title: '法律基础',
    description: '2002年《上海市轨道交通管理条例》第二十五条奠定了安检基础。',
    detail: '第二十五条 禁止乘客携带易燃、易爆、有毒和有放射性、腐蚀性的危险品乘车。轨道交通线路运营单位有权对乘客携带的物品进行安全检查，对携带危害公共安全的易燃、易爆、有毒或者有放射性、腐蚀性危险品的乘客，应当责令其出站;拒不出站的，移送公安部门依法处理。',
    highlight: false,
    source: '资料来源：豆丁网'
  },
  {
    year: '2008年',
    title: '开箱检查',
    description: '开始实行开箱检查。',
    highlight: false,
    source: '资料来源：汉网'
  },
  {
    year: '2009年',
    title: '部分封闭',
    description: '9月开始部分车站全封闭安检。',
    highlight: true,
    source: '资料来源：解放日报'
  },
  {
    year: '2010年1月',
    title: '全面封闭',
    description: '上海43个轨交站全封闭安检，拒检致严重后果将被拘。',
    highlight: false,
    source: '资料来源：中国新闻网'
  },
  {
    year: '2010年3月',
    title: '全网覆盖',
    description: '施行《上海市轨道交通运营安全管理办法》第十五条。',
    detail: '第十五条：禁止乘客携带易燃、易爆、有毒、有放射性、有腐蚀性以及其他有可能危及人身和财产安全的危险物品进站、乘车。具体危险物品目录和样式，由运营单位按照规定，在车站内通过张贴、陈列等方式予以公告。运营单位应当按照有关规定和标准，在车站内设置安全检查设施、设备，配备受过专业培训的安全检查人员，并按照规定对乘客携带的物品进行安全检查。乘客应当接受和配合安全检查；拒不接受、配合安全检查的，运营单位应当拒绝其进站、乘车。运营单位在安全检查中发现有危险物品的，应当立即采取防止危险发生的安全措施，并按照规定及时报告公安部门。安全检查人员实施安全检查时，应当佩带安全检查证；未佩带安全检查证实施检查的，乘客有权拒绝检查。乘客以外的其他人员进站、乘车的，应当遵守本条规定。',
    highlight: false,
    source: '资料来源：上海市人民政府'
  },
  {
    year: '2010年3月15日',
    title: '全面升级',
    description: '280座地铁车站全部实行进站安检，其中12座重点车站更是"逢包必检"。此举是为了迎接即将于5月1日开幕的上海世博会，确保世博会轨道交通安全。',
    highlight: false,
    source: '资料来源：新华网'
  },
  {
    year: '2010年11月',
    title: '常态化',
    description: '世博会后轨交安检常态化。',
    highlight: true,
    source: '资料来源：东方网'
  },
  {
    year: '2016年',
    title: '反恐法实施',
    description: '2016年1月1日起，《中华人民共和国反恐怖主义法》实施，为安检提供法律依据，检查标准进一步强化',
    detail: '第三十四条：大型活动承办单位以及重点目标的管理单位应当依照规定，对进入大型活动场所、机场、火车站、码头、城市轨道交通站、公路长途客运站、口岸等重点目标的人员、物品和交通工具进行安全检查。发现违禁品和管制物品，应当予以扣留并立即向公安机关报告；发现涉嫌违法犯罪人员，应当立即向公安机关报告。第三十五条  对航空器、列车、船舶、城市轨道车辆、公共电汽车等公共交通运输工具，营运单位应当依照规定配备安保人员和相应设备、设施，加强安全检查和保卫工作。第八十八条  防范恐怖袭击重点目标的管理、营运单位违反本法规定，有下列情形之一的，由公安机关给予警告，并责令改正；拒不改正的，处十万元以下罚款，并对其直接负责的主管人员和其他直接责任人员处一万元以下罚款：（一）未制定防范和应对处置恐怖活动的预案、措施的；（二）未建立反恐怖主义工作专项经费保障制度，或者未配备防范和处置设备、设施的；（三）未落实工作机构或者责任人员的；（四）未对重要岗位人员进行安全背景审查，或者未将有不适合情形的人员调整工作岗位的；（五）对公共交通运输工具未依照规定配备安保人员和相应设备、设施的；（六）未建立公共安全视频图像信息系统值班监看、信息保存使用、运行维护等管理制度的。大型活动承办单位以及重点目标的管理单位未依照规定对进入大型活动场所、机场、火车站、码头、城市轨道交通站、公路长途客运站、口岸等重点目标的人员、物品和交通工具进行安全检查的，公安机关应当责令改正；拒不改正的，处十万元以下罚款，并对其直接负责的主管人员和其他直接责任人员处一万元以下罚款。对城市轨道交通站等重点目标的人员、物品和交通工具进行安全检查。营运单位应配备安保人员和相应设备设施。',
    highlight: false,
    source: '资料来源：中国政府网'
  },
  {
    year: '2019年11月1日',
    title: '免检试点',
    description: '2019年11月1日起，虹桥火车站试点铁路换地铁免二次安检。',
    highlight: true,
    source: '资料来源：上海虹桥国际中央商务区管理委员会'
  },
  {
    year: '2024年4月4日',
    title: '上海南站免检',
    description: '2024年4月4日起，上海南站铁路出站旅客经专用通道免安检换乘地铁（金山铁路限早高峰时段免安检）。可节约20秒。',
    highlight: false,
    source: '资料来源：上观新闻'
  },
  {
    year: '2024年4月27日',
    title: '上海火车站免检',
    description: '上海火车站铁路旅客经东北出口免安检换乘3、4号线。可节约15分钟。',
    highlight: false,
    source: '资料来源：上观新闻'
  },
  {
    year: '2025年5月30日',
    title: '规范化管理',
    description: '2025年5月30日起施行《上海市轨道交通安检工作规范》，有效期至2030年5月29日。其中，对安检工作进规定了详细标准。',
    detail: '第十四条：运营企业应根据下列标准开展安检工作：（一）使用安检机对乘客携带的各类行李物品进行检查。在大客流等情况下，运营企业可根据工作方案，使用金属探测器、人工检查等方式对乘客携带的小件行李物品开展检查，提高安检通行速度；（二）使用金属探测器、智能安检门等设备对乘客及其随身物品进行安检。对女性乘客应当由女性安检人员进行安检； （三）使用人工甄别方式对乘客携带的液体进行检查，无法排除可疑情形的，使用液态危险品检查仪进行复查。对于瓶内液体颜色明显与外包装标识不符的，应重点甄别检查。对上述方式无法排除可疑情形的，应采取人工甄别结合设备辅助的方式进行复查。复查后仍无法排除可疑情形的，立即向公安机关报告。第二十九条  本规范所称"随身物品"，是指乘客随身存放的物品。"行李物品"，包含包、箱、盒、袋等物品。其中，同时符合"长不超过40cm"、"宽不超过30cm"、"高不超过20cm"的行李物品为小件行李物品，其他为大件行李物品。第三十条  本市行政区域内磁浮交通、轨道交通市域线的安检工作参照本规范执行。对安检工作制定详细标准：使用安检机检查行李物品、金属探测器检查随身物品、人工甄别液体等。',
    highlight: true,
    source: '资料来源：上海市公安局'
  }
]

// 自动滚动函数
const autoScroll = () => {
  if (!timelineRef.value || !isAutoScrolling.value || isPausedByHover.value || isWaitingForRestart.value) return
  
  const container = timelineRef.value
  const scrollStep = scrollSpeed.value * 0.5
  
  container.scrollTop += scrollStep
  
  // 如果滚动到底部，等待2秒后重新开始
  if (container.scrollTop >= container.scrollHeight - container.clientHeight) {
    isWaitingForRestart.value = true
    
    setTimeout(() => {
      if (isAutoScrolling.value && !isPausedByHover.value) {
        container.scrollTop = 0
        isWaitingForRestart.value = false
        // 继续滚动
        animationId.value = requestAnimationFrame(autoScroll)
      } else {
        isWaitingForRestart.value = false
      }
    }, 2000) // 等待2秒
    
    return // 停止当前循环
  }
  
  animationId.value = requestAnimationFrame(autoScroll)
}

// 鼠标悬停时暂停
const pauseOnHover = () => {
  isPausedByHover.value = true
  // 停止当前动画帧
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = null
  }
}

// 鼠标离开时恢复
const resumeOnLeave = () => {
  isPausedByHover.value = false
  // 如果自动滚动开启且当前没有运行动画，且不在等待重启状态，则重新开始
  if (isAutoScrolling.value && !isWaitingForRestart.value) {
    autoScroll()
  }
}

// 组件挂载时自动开始滚动
onMounted(() => {
  setTimeout(() => {
    if (isAutoScrolling.value) {
      autoScroll()
    }
  }, 1000) // 延迟1秒开始自动滚动
})

// 组件卸载时清理动画
onUnmounted(() => {
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600;700&display=swap');

.metro-security-timeline-container {
  position: relative;
  width: 100%;
}

.security-timeline {
  width: 100%;
  height: 500px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
  overflow-y: auto;
  font-family: 'Noto Serif SC', serif;
  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* 隐藏 Webkit 浏览器的滚动条 */
.security-timeline::-webkit-scrollbar {
  display: none;
}

.timeline-container {
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  gap: 24px;
}

.timeline-item {
  position: relative;
  width: 100%;
}

.timeline-content {
  background: white;
  padding: 24px 28px 48px 28px;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;
}

.timeline-item.highlight .timeline-content {
  background: linear-gradient(135deg, #fef2f2, #ffffff);
  border-color: #fecaca;
  box-shadow: 0 8px 32px rgba(220, 38, 38, 0.15);
}

.timeline-content:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.timeline-year {
  font-size: 20px;
  font-weight: 700;
  color: #0085C3;
  margin-bottom: 12px;
  position: relative;
  display: inline-block;
}

.timeline-year::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, #0085C3, transparent);
  border-radius: 1px;
}

.timeline-item.highlight .timeline-year {
  color: #dc2626;
}

.timeline-item.highlight .timeline-year::after {
  background: linear-gradient(to right, #dc2626, transparent);
}

.timeline-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  font-family: 'Noto Serif SC', serif;
}

.timeline-desc {
  font-size: 15px;
  color: #374151;
  line-height: 1.7;
  margin-bottom: 16px;
  font-weight: 500;
  font-family: 'Noto Serif SC', serif;
}

.timeline-detail {
  font-size: 13px;
  color: #6b7280;
  background: #f9fafb;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  font-weight: 400;
  line-height: 1.6;
  font-family: 'Noto Serif SC', serif;
  position: relative;
}

.timeline-detail::before {
  content: '📋';
  position: absolute;
  top: 16px;
  left: -8px;
  background: white;
  padding: 4px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
}

.card-data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 11px;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 12px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(229, 231, 235, 0.8);
  font-weight: 500;
  z-index: 10;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .timeline-container {
    padding: 0 16px;
  }
  
  .timeline-content {
    padding: 20px 24px 40px 24px;
  }
  
  .timeline-year {
    font-size: 18px;
  }
  
  .timeline-title {
    font-size: 16px;
  }
  
  .timeline-desc {
    font-size: 14px;
  }
}
</style>